// 📄 pages/Proyectos/components/HorasSection.tsx
// "Mis Horas": cada usuario ve y gestiona únicamente sus propios registros
// de horas trabajadas en el proyecto (ver registroHorasService.ts).
import React, { useEffect, useMemo, useState } from 'react';
import { clasificacionDe } from '../modulosConfig.ts';
import { Plus, RefreshCw, Clock } from 'lucide-react';
import { useProject } from '../../../context/ProjectContext.tsx';
import { useAuth } from '../../../context/AuthContext.tsx';
import { getMisRegistros, RegistroHoras } from '../../../services/registroHorasService.ts';
import { SOLUCIONES } from '../solucionesConfig.ts';
import RegistroHorasCard from './RegistroHorasCard.tsx';
import RegistroHorasFormModal from './RegistroHorasFormModal.tsx';

const primerDiaDelMes = (): string => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const ultimoDiaDelMes = (): string => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
};

export default function HorasSection() {
  const { project, projectId, esAdminObservador } = useProject();
  const { user } = useAuth();
  const [registros, setRegistros] = useState<RegistroHoras[]>([]);
  const [loading, setLoading] = useState(true);
  const [desde, setDesde] = useState(primerDiaDelMes());
  const [hasta, setHasta] = useState(ultimoDiaDelMes());
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<RegistroHoras | null>(null);

  // Con SAP Ariba se clasifica por solución; sin SAP, por área del proyecto (ver modulosConfig.ts).
  const { modoArea, opciones: solucionesActivas } = clasificacionDe(project);

  const loadRegistros = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await getMisRegistros(projectId, user.uid);
      setRegistros(data);
    } catch (err) {
      console.error('Error cargando registros de horas:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId && user) loadRegistros();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, user?.uid]);

  const enRango = useMemo(() => {
    const desdeMs = new Date(`${desde}T00:00:00`).getTime();
    const hastaMs = new Date(`${hasta}T23:59:59`).getTime();
    return registros.filter(r => {
      const ms = r.fecha?.toMillis?.() ?? 0;
      return ms >= desdeMs && ms <= hastaMs;
    });
  }, [registros, desde, hasta]);

  const totales = useMemo(() => {
    const horas = enRango.reduce((acc, r) => acc + r.horas, 0);
    const pendiente = enRango
      .filter(r => r.estadoFacturacion === 'Pendiente')
      .reduce((acc, r) => acc + r.totalCalculado, 0);
    const facturado = enRango
      .filter(r => r.estadoFacturacion === 'Facturado')
      .reduce((acc, r) => acc + r.totalCalculado, 0);
    return { horas, pendiente, facturado };
  }, [enRango]);

  const handleSaved = () => {
    setFormOpen(false);
    setEditing(null);
    loadRegistros();
  };

  const handleEdit = (registro: RegistroHoras) => {
    setEditing(registro);
    setFormOpen(true);
  };

  const handleNuevo = () => {
    setEditing(null);
    setFormOpen(true);
  };

  if (!project || !user) return null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SummaryTile label="Horas en el rango" value={totales.horas.toLocaleString('es', { maximumFractionDigits: 2 })} />
        <SummaryTile
          label="Pendiente por facturar"
          value={`$${totales.pendiente.toLocaleString('es', { maximumFractionDigits: 2 })}`}
          accent="text-yellow-400"
        />
        <SummaryTile
          label="Facturado"
          value={`$${totales.facturado.toLocaleString('es', { maximumFractionDigits: 2 })}`}
          accent="text-emerald-400"
        />
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap text-xs text-gray-400">
          <label className="flex items-center gap-2">
            Desde
            <input
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
              className="bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:outline-none"
            />
          </label>
          <label className="flex items-center gap-2">
            Hasta
            <input
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
              className="bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:outline-none"
            />
          </label>
        </div>

        {!esAdminObservador && (
          <button
            onClick={handleNuevo}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-xl text-sm font-semibold text-[#fff] transition-all"
          >
            <Plus className="w-4 h-4" /> Registrar Horas
          </button>
        )}
      </div>

      {loading ? (
        <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
          <p className="text-sm">Cargando registros...</p>
        </div>
      ) : enRango.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <Clock className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">
            {registros.length === 0
              ? 'Todavía no has registrado horas en este proyecto.'
              : 'Ningún registro coincide con el rango seleccionado.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {enRango.map((r) => (
            <RegistroHorasCard
              key={r.id}
              projectId={projectId}
              registro={r}
              solucionLabel={SOLUCIONES[r.solucionRelacionada]?.label || r.solucionRelacionada}
              onEdit={() => handleEdit(r)}
              onChanged={loadRegistros}
            />
          ))}
        </div>
      )}

      {formOpen && (
        <RegistroHorasFormModal
          projectId={projectId}
          uid={user.uid}
          solucionesActivas={solucionesActivas}
          modoArea={modoArea}
          registro={editing}
          onClose={() => { setFormOpen(false); setEditing(null); }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

function SummaryTile({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
      <p className="text-xs text-gray-400 mb-1">{label}</p>
      <p className={`text-2xl font-light ${accent || 'text-white'}`}>{value}</p>
    </div>
  );
}
